import { useEffect, useRef, useState } from "react";

/**
 * How long the answer took, for the legacy turn produced in THIS session. The
 * assistant engine sends its own seconds: one of its runs can span several streams.
 *
 * The transcript does not persist a generation time, so the duration is timed
 * client-side around the stream and reported for the message it produced. Turns
 * restored from the database therefore carry none — by construction, not by
 * accident: the indicator disappears on reload, which is why the caller takes
 * `durationLabel: string | null` rather than defaulting to a made-up number.
 */
export type StreamDuration = { messageId: string; seconds: number };

export function useStreamDuration(
  isStreaming: boolean,
  // The message the stream is currently writing (the last one), or null on an
  // empty conversation.
  lastMessageId: string | null
): StreamDuration | null {
  const startedAtRef = useRef<number | null>(null);
  const [duration, setDuration] = useState<StreamDuration | null>(null);
  // Read inside the effect without making it re-run on every new token.
  const lastMessageIdRef = useRef(lastMessageId);
  lastMessageIdRef.current = lastMessageId;

  useEffect(() => {
    if (isStreaming) {
      if (startedAtRef.current === null)
        startedAtRef.current = performance.now();
      return;
    }
    const startedAt = startedAtRef.current;
    startedAtRef.current = null;
    if (startedAt === null) return;
    const messageId = lastMessageIdRef.current;
    if (messageId === null) return;
    setDuration({
      messageId,
      seconds: Math.max(1, Math.round((performance.now() - startedAt) / 1000)),
    });
  }, [isStreaming]);

  return duration;
}
